import { Tooltip, TooltipTrigger } from "@artis/ui";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HexColorPicker } from "react-colorful";
import cn from "mcn";
import { EditIcon } from "../icons/EditIcon";
import { IoAdd } from "react-icons/io5";
import { CloseIcon } from "../icons/CloseIcon";
import { Button, IconButton } from "../ui";
import ClickAwayListener from "../components/ClickAwayListener";

const DEFAULT_COLOURS = [
  "#ffa166",
  "#ffff99",
  "#65e6d9",
  "#f591dd",
  "#99c7ff",
  "#48494b",
  "#808588",
  "#d9dddc",
  "#00a651",
  "#dc2626",
  "#1a4088",
];

const ColorSwatch = ({
  color,
  selected,
  onClick,
  isNoColor,
  onContextMenu,
  isFocused,
}: {
  color: string;
  selected?: boolean;
  onClick: () => void;
  isNoColor?: boolean;
  onContextMenu?: (e: React.MouseEvent) => void;
  isFocused?: boolean;
}) => {
  const transparentBg =
    "linear-gradient(45deg, #E5E5E5 25%, transparent 25%, transparent 75%, #E5E5E5 75%, #E5E5E5 100%),linear-gradient(45deg, #E5E5E5 25%, #FFFFFF 25%, #FFFFFF 75%, #E5E5E5 75%, #E5E5E5 100%)";

  const colorId = color.replace("#", "");

  return (
    <>
      {!isNoColor && !selected && (
        <style>{`
          .color-swatch-${colorId}:hover .swatch-outer {
            background-color: ${color} !important;
            border: 1px solid ${color} !important;
          }
          .color-swatch-${colorId}:hover .swatch-inner {
            background-color: ${color} !important;
          }
        `}</style>
      )}
      {!isNoColor && selected && (
        <style>{`
          .color-swatch-${colorId} {
            transition: transform 0.15s ease-in-out;
          }
          .color-swatch-${colorId}:hover {
            transform: scale(1.08);
          }
        `}</style>
      )}
      {!isNoColor && isFocused && (
        <style>{`
          .color-swatch-${colorId} .swatch-outer {
            background-color: ${color} !important;
            border: 2px solid rgba(0, 0, 0, 0.25) !important;
          }
          .color-swatch-${colorId} .swatch-inner {
            background-color: ${color} !important;
          }
        `}</style>
      )}
      {!isNoColor && !selected && (
        <style>{`
          .color-swatch-${colorId} {
            transition: transform 0.15s ease-in-out;
          }
          .color-swatch-${colorId} .swatch-outer {
            transition: border 0.15s ease-in-out;
          }
          .color-swatch-${colorId}:hover {
            transform: scale(1.08);
          }
          .color-swatch-${colorId}:hover .swatch-outer {
            border: 1px solid rgba(0, 0, 0, 0.25) !important;
          }
        `}</style>
      )}
      {isNoColor && (
        <style>{`
          .color-swatch-no-color {
            transition: transform 0.15s ease-in-out;
            transform-origin: center center;
          }
          .color-swatch-no-color:hover {
            transform: scale(1.08);
          }
          .color-swatch-no-color .no-color-container {
            width: 25px;
            height: 25px;
          }
          .color-swatch-no-color .no-color-icon {
            width: 17px;
            height: 17px;
          }
        `}</style>
      )}
      <TooltipTrigger>
        <button
          type="button"
          onClick={onClick}
          data-testid={isNoColor ? "color-swatch-no-color" : `color-swatch-${colorId}`}
          onContextMenu={onContextMenu}
          className={cn(
            "cursor-pointer flex items-center justify-center shrink-0 w-[27px] h-[27px] rounded-full overflow-visible p-[var(--spacing-scale-small-px,1px)] relative swatch",
            !isNoColor && `color-swatch-${colorId}`,
            isNoColor && "color-swatch-no-color",
          )}
        >
          <div
            className={cn(
              "swatch-outer rounded-full flex items-center justify-center transition-all shrink-0 border border-solid relative",
              selected && !isNoColor
                ? "w-[25px] h-[25px] p-[var(--spacing-scale-small-spacing,3px)] bg-[var(--toast-default,#f8f8ff)]"
                : "w-[25px] h-[25px]",
              !selected && !isNoColor && "border-transparent",
            )}
            style={{
              borderColor: selected && !isNoColor ? color : undefined,
              backgroundColor: !selected && !isNoColor ? color : undefined,
            }}
          >
            {selected && !isNoColor ? (
              <div
                className="swatch-inner rounded-full w-[19px] h-[19px] min-w-[19px] min-h-[19px] shrink-0 relative z-[1]"
                style={{
                  backgroundColor: color,
                }}
              />
            ) : isNoColor ? (
              <div
                className="no-color-container w-[25px] h-[25px] rounded-full flex items-center justify-center border border-[var(--outline-default,#BBB)] bg-[length:6px_6px] bg-[position:0px_0px,3px_3px]"
                style={{
                  backgroundImage: transparentBg,
                }}
              >
                {selected && (
                  <svg
                    className="no-color-icon text-[var(--fixed-artis-black)]"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    role="img"
                    aria-label="No color"
                  >
                    <path
                      d="M12 4L4 12M4 4L12 12"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
            ) : null}
          </div>
        </button>
        <Tooltip>{isNoColor ? "No colour" : onContextMenu ? `${color} (right-click for options)` : color}</Tooltip>
      </TooltipTrigger>
    </>
  );
};

export function ColorPicker({
  value,
  onChange,
  position,
  disabled,
  additionalSettings,
  savedColours = [],
  onSavedColoursChange,
  open,
  onOpenChange,
  anchorRef,
}: {
  value: string;
  onChange: (color: string) => void;
  position: "top" | "bottom";
  disabled?: boolean;
  additionalSettings?: React.ReactNode;
  savedColours?: string[];
  onSavedColoursChange?: (colours: string[]) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  anchorRef?: React.RefObject<HTMLElement>;
}) {
  "use no memo";

  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalIsOpen;
  const setIsOpen = useCallback(
    (newOpen: boolean) => {
      if (open !== undefined) {
        onOpenChange?.(newOpen);
      } else {
        setInternalIsOpen(newOpen);
      }
    },
    [open, onOpenChange],
  );

  const [isAddingColor, setIsAddingColor] = useState(false);
  const [newColor, setNewColor] = useState("#3B82F6");
  const [popupPosition, setPopupPosition] = useState<{ top: number; left: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    color: string;
  } | null>(null);
  const [isEditingColor, setIsEditingColor] = useState(false);
  const [editingColor, setEditingColor] = useState<string | null>(null);
  const [originalColorBeforeEdit, setOriginalColorBeforeEdit] = useState<string | null>(null);

  const transparentBg =
    "linear-gradient(45deg, #E5E5E5 25%, transparent 25%, transparent 75%, #E5E5E5 75%, #E5E5E5 100%),linear-gradient(45deg, #E5E5E5 25%, #FFFFFF 25%, #FFFFFF 75%, #E5E5E5 75%, #E5E5E5 100%)";

  const isNoColor = value === "inherit" || !value;

  // Compute the left position of the add/edit sub-panel.
  // Use the actual rendered width of the main popup (via ref) so the sub-panel
  // never overlaps it. Default: open to the RIGHT; flip LEFT if no room.
  const subPanelWidth = 176;
  const subPanelLeft = popupPosition
    ? (() => {
        const mainWidth = popupRef.current?.offsetWidth ?? 216;
        const rightPos = popupPosition.left + mainWidth + 8;
        return rightPos + subPanelWidth <= window.innerWidth - 8 ? rightPos : popupPosition.left - subPanelWidth - 8;
      })()
    : 0;

  const handleClickAway = useCallback(() => {
    if (!isAddingColor && !isEditingColor && !contextMenu) {
      setIsOpen(false);
    }
  }, [setIsOpen, isAddingColor, isEditingColor, contextMenu]);

  const handleAddColorClickAway = useCallback(() => {
    setIsAddingColor(false);
    if (originalColorBeforeEdit !== null) {
      onChange(originalColorBeforeEdit);
      setOriginalColorBeforeEdit(null);
    }
  }, [originalColorBeforeEdit, onChange]);

  useEffect(() => {
    if (isControlled && isOpen && anchorRef?.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const popupWidth = 188;
      const savedColoursRows = Math.ceil((savedColours.length + 1) / 7);
      const popupHeight = 120 + savedColoursRows * 21;

      let left = rect.left;
      let top = position === "bottom" ? rect.bottom + 8 : rect.top - popupHeight - 8;

      if (left < 8) left = 8;
      if (left + popupWidth > window.innerWidth - 8) {
        left = window.innerWidth - popupWidth - 8;
      }
      if (top < 8) top = 8;
      if (top + popupHeight > window.innerHeight - 8) {
        top = window.innerHeight - popupHeight - 8;
      }

      setPopupPosition({ top, left });
    }
  }, [isControlled, isOpen, anchorRef, position, savedColours.length]);

  const handleToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;

    e.stopPropagation();

    if (!isOpen) {
      const rect = e.currentTarget.getBoundingClientRect();
      const popupWidth = 188;
      const savedColoursRows = Math.ceil((savedColours.length + 1) / 7);
      const popupHeight = 120 + savedColoursRows * 21;

      let left = rect.left - popupWidth + 30;
      if (left < 8) left = 8;
      if (left + popupWidth > window.innerWidth - 8) {
        left = window.innerWidth - popupWidth - 8;
      }

      // Try above or below based on the `position` prop; flip if it goes off-screen.
      let top = position === "bottom" ? rect.bottom + 8 : rect.top - popupHeight - 8;
      if (top < 8) top = 8;
      if (top + popupHeight > window.innerHeight - 8) {
        top = window.innerHeight - popupHeight - 8;
      }

      setPopupPosition({ top, left });
      setIsOpen(true);
    } else {
      setIsOpen(false);
      setIsAddingColor(false);
    }
  };

  const handleColorSelect = (color: string) => {
    onChange(color);
  };

  const MAX_SAVED_COLOURS = 11;

  const handleAddColor = () => {
    if (newColor && !savedColours.includes(newColor.toLowerCase())) {
      let updated = [...savedColours, newColor.toLowerCase()];
      if (updated.length > MAX_SAVED_COLOURS) {
        updated = updated.slice(updated.length - MAX_SAVED_COLOURS);
      }
      onSavedColoursChange?.(updated);
    }
    setIsAddingColor(false);
  };

  const handleUpdateColor = () => {
    if (editingColor && newColor && editingColor !== newColor.toLowerCase()) {
      const updated = savedColours.map((c) => (c === editingColor ? newColor.toLowerCase() : c));
      onSavedColoursChange?.(updated);
    }
    setIsEditingColor(false);
    setEditingColor(null);
    setOriginalColorBeforeEdit(null);
  };

  const handleCancelEdit = useCallback(() => {
    setIsEditingColor(false);
    setEditingColor(null);
    if (originalColorBeforeEdit !== null) {
      onChange(originalColorBeforeEdit);
      setOriginalColorBeforeEdit(null);
    }
  }, [originalColorBeforeEdit, onChange]);

  const handleColorChange = useCallback(
    (color: string) => {
      setNewColor(color);
      // Apply color temporarily for preview
      onChange(color);
    },
    [onChange],
  );

  const handleDeleteColor = (color: string) => {
    const updated = savedColours.filter((c) => c !== color);
    onSavedColoursChange?.(updated);
    setContextMenu(null);
  };

  return (
    <div ref={containerRef} className="relative flex items-center gap-[1px]">
      {!isControlled && (
        <>
          <button
            onClick={handleToggle}
            className={cn("rounded-lg relative overflow-hidden shrink-0 group picker", {
              "cursor-not-allowed opacity-50": disabled,
            })}
            style={{
              width: "22px",
              height: "22px",
              backgroundColor: isNoColor ? "" : (value ?? ""),
              backgroundImage: isNoColor ? transparentBg : "",
              backgroundSize: isNoColor ? "14.67px 14.67px" : "",
              backgroundPosition: isNoColor ? "0px 0px, 7.33px 7.33px" : "",
              border: isNoColor ? "1.5px solid var(--outline-opaque)" : "",
            }}
            type="button"
            disabled={disabled}
            data-testid="color-picker-button"
          >
            {isNoColor && (
              <div className="absolute inset-0 bg-[rgba(0,0,0,0.06)] opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity shrink-0 size-[22px]" />
            )}
          </button>
          <IconButton type="button" className="ml-1" onClick={handleToggle} disabled={disabled}>
            <EditIcon className="text-neutral" />
          </IconButton>
        </>
      )}

      {isOpen &&
        popupPosition &&
        createPortal(
          <ClickAwayListener onClickAway={handleClickAway}>
            <div
              ref={popupRef}
              onMouseDown={(e) => e.stopPropagation()}
              className="fixed z-[9999] flex flex-col items-center w-2xs max-w-2xs p-[var(--spacing-scale-small-400,12px)] gap-[var(--spacing-scale-small-300,9px)] rounded-[var(--border-radius-rounded-lg,6px)] outline outline-1 outline-[var(--outline-default,#BBB)] bg-[var(--card-muted)]"
              style={{
                top: popupPosition.top,
                left: popupPosition.left,
                boxShadow:
                  "0 4px 6px -1px var(--shadow-md, rgba(100, 100, 100, 0.10)), 0 2px 4px -1px var(--shadow-sm, rgba(100, 100, 100, 0.06))",
              }}
            >
              <div className="flex justify-center items-center self-stretch h-[18px] gap-[var(--spacing-scale-small-300,9px)]">
                <span className="flex-1 text-[var(--foreground-default,#101010)] text-[var(--font-size-lg,13.5px)] font-[var(--font-weights-semibold,600)] leading-[var(--line-height-text-lg,21px)]">
                  Fill colour
                </span>
                <CloseIcon
                  onClick={() => {
                    setIsOpen(false);
                    setIsAddingColor(false);
                  }}
                />
              </div>

              <div className="flex flex-col items-start self-stretch gap-[var(--spacing-scale-small-400,12px)]">
                <div className="flex flex-col gap-[var(--spacing-scale-small-100,3px)]">
                  <p className="text-[var(--toast-foreground,#101010)] text-[var(--font-size-base,12px)] font-[var(--font-weights-medium,500)] leading-[var(--line-height-text-base,18px)]">
                    Default colours
                  </p>
                  <div className="flex gap-[var(--spacing-scale-small-200,6px)] items-center flex-wrap">
                    <ColorSwatch color="" isNoColor selected={isNoColor} onClick={() => handleColorSelect("inherit")} />
                    {DEFAULT_COLOURS.map((color) => (
                      <ColorSwatch
                        key={color}
                        color={color}
                        selected={value?.toLowerCase() === color.toLowerCase()}
                        onClick={() =>
                          value?.toLowerCase() === color.toLowerCase()
                            ? handleColorSelect("inherit")
                            : handleColorSelect(color)
                        }
                      />
                    ))}
                  </div>
                </div>

                <div className="flex flex-col gap-[var(--spacing-scale-small-100,3px)]">
                  <p className="text-[var(--toast-foreground,#101010)] text-[var(--font-size-base,12px)] font-[var(--font-weights-medium,500)] leading-[var(--line-height-text-base,18px)]">
                    Saved colours
                  </p>
                  <div className="flex flex-wrap gap-[var(--spacing-scale-small-200,6px)] items-center">
                    {savedColours.map((color) => (
                      <ColorSwatch
                        key={color}
                        color={color}
                        selected={value?.toLowerCase() === color.toLowerCase()}
                        isFocused={
                          editingColor?.toLowerCase() === color.toLowerCase() ||
                          contextMenu?.color?.toLowerCase() === color.toLowerCase()
                        }
                        onClick={() =>
                          value?.toLowerCase() === color.toLowerCase()
                            ? handleColorSelect("inherit")
                            : handleColorSelect(color)
                        }
                        onContextMenu={(e) => {
                          e.preventDefault();
                          setContextMenu({
                            x: e.clientX,
                            y: e.clientY,
                            color: color,
                          });
                        }}
                      />
                    ))}
                    <IconButton
                      variant="primary"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOriginalColorBeforeEdit(value);
                        setIsAddingColor(true);
                      }}
                      className="!w-[27px] !h-[27px] !min-w-[27px] !min-h-[27px] !p-0 flex items-center justify-center"
                    >
                      <IoAdd className="size-[16px]" />
                    </IconButton>
                  </div>
                </div>
              </div>

              {additionalSettings && (
                <div className="py-2 border-t border-outline-variant w-[calc(100%+24px)] -mx-3">
                  {additionalSettings}
                </div>
              )}
            </div>
          </ClickAwayListener>,
          document.body,
        )}

      {contextMenu &&
        createPortal(
          <ClickAwayListener onClickAway={() => setContextMenu(null)}>
            <div
              className="fixed z-[10001] border-outline border rounded-md bg-card shadow-md overflow-hidden select-none flex flex-col"
              style={{
                top: contextMenu.y,
                left: contextMenu.x,
                paddingTop: "2px",
                paddingBottom: "2px",
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="w-full">
                <Button
                  onClick={() => {
                    setEditingColor(contextMenu.color);
                    setNewColor(contextMenu.color);
                    setOriginalColorBeforeEdit(value);
                    setIsEditingColor(true);
                    setContextMenu(null);
                  }}
                  className={cn(
                    "!w-full !px-4 !py-2 !rounded-none !justify-start !items-center",
                    "!bg-card !text-foreground hover:!bg-[var(--neutral-tonal)]",
                    "!text-[calc(1px+var(--webapp-font-size-sm))] !font-normal !leading-none",
                  )}
                >
                  Edit
                </Button>
              </div>
              <div className="w-full">
                <Button
                  onClick={() => handleDeleteColor(contextMenu.color)}
                  className={cn(
                    "!w-full !px-4 !py-2 !rounded-none !justify-start !items-center",
                    "!bg-card !text-foreground hover:!bg-[var(--neutral-tonal)]",
                    "!text-[calc(1px+var(--webapp-font-size-sm))] !font-normal !leading-none",
                  )}
                >
                  Delete
                </Button>
              </div>
            </div>
          </ClickAwayListener>,
          document.body,
        )}

      {isAddingColor &&
        createPortal(
          <ClickAwayListener onClickAway={handleAddColorClickAway}>
            <div
              className="fixed z-[10000] flex flex-col items-center w-[176px] h-[260px] p-[var(--spacing-scale-small-400,12px)] gap-[var(--spacing-scale-small-400,12px)] rounded-[var(--border-radius-rounded-lg,6px)] border border-solid border-[var(--outline-default,#BBB)] bg-[var(--card-muted)] shadow-[0_10px_15px_-3px_var(--shadow-md,rgba(100,100,100,0.10)),0_4px_6px_-2px_var(--shadow-sm,rgba(100,100,100,0.06))]"
              style={{
                top: popupPosition ? popupPosition.top : 0,
                left: subPanelLeft,
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex items-center w-full gap-[var(--spacing-scale-small-300,9px)]">
                <span className="flex-1 text-[var(--foreground-default,#101010)] text-[var(--font-size-lg,13.5px)] font-[var(--font-weights-semibold,600)] leading-[var(--line-height-text-lg,21px)]">
                  Add new colour
                </span>
                <CloseIcon
                  onClick={() => {
                    setIsAddingColor(false);
                    if (originalColorBeforeEdit !== null) {
                      onChange(originalColorBeforeEdit);
                      setOriginalColorBeforeEdit(null);
                    }
                  }}
                />
              </div>

              <div className="flex flex-col items-center justify-center w-full gap-[var(--spacing-scale-small-300,9px)]">
                <style>
                  {`
                .color-picker-with-gap .react-colorful {
                  gap: 9px;
                  height: auto !important;
                  touch-action: none;
                }
                .color-picker-with-gap .react-colorful__hue {
                  height: 12px;
                  border-radius: 6px;
                }
                .color-picker-with-gap .react-colorful__hue-pointer {
                  width: 16px;
                  height: 16px;
                  border-radius: 50%;
                  border: 2px solid white;
                  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
                }
                .color-picker-with-gap .react-colorful__saturation {
                  border-radius: 6px;
                  width: 148px !important;
                  height: 100px !important;
                  padding-bottom: 0 !important;
                }
                .color-picker-with-gap .react-colorful__saturation-pointer {
                  width: 16px;
                  height: 16px;
                  border-radius: 50%;
                  border: 2px solid white;
                  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
                }
              `}
                </style>
                <div
                  className="color-picker-with-gap"
                  onPointerDown={(e) => {
                    const target = e.target as HTMLElement;
                    if (target.closest(".react-colorful__saturation") || target.closest(".react-colorful__hue")) {
                      target.setPointerCapture(e.pointerId);
                    }
                  }}
                >
                  <HexColorPicker color={newColor} onChange={handleColorChange} style={{ width: "148px" }} />
                </div>

                <div className="flex items-center w-full gap-[var(--spacing-scale-small-400,12px)] px-[var(--spacing-scale-small-spacing,3px)] py-0">
                  <span className="text-[var(--toast-foreground,#101010)] text-[var(--font-size-base,12px)] font-[var(--font-weights-medium,500)] leading-[var(--line-height-text-base,18px)] w-[35px] shrink-0">
                    Hex:
                  </span>
                  <div className="flex items-center justify-center overflow-hidden min-w-[80px] max-w-[150px] px-[var(--spacing-scale-small-200,6px)] py-[var(--spacing-scale-small-150,4.5px)] gap-[var(--spacing-scale-small-150,4.5px)] rounded-[var(--border-radius-rounded-lg,6px)] border border-solid border-[var(--outline-default,#BBB)] bg-[var(--surface-1,white)]">
                    <input
                      type="text"
                      value={newColor}
                      onChange={(e) => {
                        let val = e.target.value;
                        if (!val.startsWith("#")) val = `#${val}`;
                        handleColorChange(val.slice(0, 7));
                      }}
                      className="flex-1 focus:outline-none min-w-0 text-[var(--font-size-base,12px)] text-[var(--toast-foreground,#101010)] bg-transparent"
                    />
                    <div
                      className="shrink-0 rounded-[var(--border-radius-rounded-lg,6px)] w-[22px] h-[22px] border border-solid border-[var(--outline-opaque,rgba(0,0,0,0.25))] shadow-[0px_1px_2px_0px_var(--shadow-sm,rgba(100,100,100,0.06))]"
                      style={{
                        backgroundColor: newColor,
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end w-full gap-4">
                <Button
                  buttonType="cancel"
                  onClick={() => {
                    setIsAddingColor(false);
                    if (originalColorBeforeEdit !== null) {
                      onChange(originalColorBeforeEdit);
                      setOriginalColorBeforeEdit(null);
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  buttonType="save"
                  onClick={() => {
                    handleAddColor();
                    setOriginalColorBeforeEdit(null);
                  }}
                >
                  Save
                </Button>
              </div>
            </div>
          </ClickAwayListener>,
          document.body,
        )}

      {isEditingColor &&
        editingColor &&
        createPortal(
          <ClickAwayListener onClickAway={handleCancelEdit}>
            <div
              className="fixed z-[10000] flex flex-col items-center w-[176px] h-[260px] p-[var(--spacing-scale-small-400,12px)] gap-[var(--spacing-scale-small-400,12px)] rounded-[var(--border-radius-rounded-lg,6px)] border border-solid border-[var(--outline-default,#BBB)] bg-[var(--card-muted)] shadow-[0_10px_15px_-3px_var(--shadow-md,rgba(100,100,100,0.10)),0_4px_6px_-2px_var(--shadow-sm,rgba(100,100,100,0.06))]"
              style={{
                top: popupPosition ? popupPosition.top : 0,
                left: subPanelLeft,
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex items-center w-full gap-[var(--spacing-scale-small-300,9px)]">
                <span className="flex-1 text-[var(--foreground-default,#101010)] text-[var(--font-size-lg,13.5px)] font-[var(--font-weights-semibold,600)] leading-[var(--line-height-text-lg,21px)]">
                  Edit saved colour
                </span>
                <CloseIcon onClick={handleCancelEdit} />
              </div>

              <div className="flex flex-col items-center justify-center w-full gap-[var(--spacing-scale-small-300,9px)]">
                <style>
                  {`
                .color-picker-with-gap .react-colorful {
                  gap: 9px;
                  height: auto !important;
                  touch-action: none;
                }
                .color-picker-with-gap .react-colorful__hue {
                  height: 12px;
                  border-radius: 6px;
                }
                .color-picker-with-gap .react-colorful__hue-pointer {
                  width: 16px;
                  height: 16px;
                  border-radius: 50%;
                  border: 2px solid white;
                  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
                }
                .color-picker-with-gap .react-colorful__saturation {
                  border-radius: 6px;
                  width: 148px !important;
                  height: 100px !important;
                  padding-bottom: 0 !important;
                }
                .color-picker-with-gap .react-colorful__saturation-pointer {
                  width: 16px;
                  height: 16px;
                  border-radius: 50%;
                  border: 2px solid white;
                  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
                }
              `}
                </style>
                <div
                  className="color-picker-with-gap"
                  onPointerDown={(e) => {
                    const target = e.target as HTMLElement;
                    if (target.closest(".react-colorful__saturation") || target.closest(".react-colorful__hue")) {
                      target.setPointerCapture(e.pointerId);
                    }
                  }}
                >
                  <HexColorPicker color={newColor} onChange={handleColorChange} style={{ width: "148px" }} />
                </div>

                <div className="flex items-center w-full gap-[var(--spacing-scale-small-400,12px)] px-[var(--spacing-scale-small-spacing,3px)] py-0">
                  <span className="text-[var(--toast-foreground,#101010)] text-[var(--font-size-base,12px)] font-[var(--font-weights-medium,500)] leading-[var(--line-height-text-base,18px)] w-[35px] shrink-0">
                    Hex:
                  </span>
                  <div className="flex items-center justify-center overflow-hidden min-w-[80px] max-w-[150px] px-[var(--spacing-scale-small-200,6px)] py-[var(--spacing-scale-small-150,4.5px)] gap-[var(--spacing-scale-small-150,4.5px)] rounded-[var(--border-radius-rounded-lg,6px)] border border-solid border-[var(--outline-default,#BBB)] bg-[var(--surface-1,white)]">
                    <input
                      type="text"
                      value={newColor}
                      onChange={(e) => {
                        let val = e.target.value;
                        if (!val.startsWith("#")) val = `#${val}`;
                        handleColorChange(val.slice(0, 7));
                      }}
                      className="flex-1 focus:outline-none min-w-0 text-[var(--font-size-base,12px)] text-[var(--toast-foreground,#101010)] bg-transparent"
                    />
                    <div
                      className="shrink-0 rounded-[var(--border-radius-rounded-lg,6px)] w-[22px] h-[22px] border border-solid border-[var(--outline-opaque,rgba(0,0,0,0.25))] shadow-[0px_1px_2px_0px_var(--shadow-sm,rgba(100,100,100,0.06))]"
                      style={{
                        backgroundColor: newColor,
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end w-full gap-4">
                <Button buttonType="cancel" onClick={handleCancelEdit}>
                  Cancel
                </Button>
                <Button buttonType="save" onClick={handleUpdateColor}>
                  Save
                </Button>
              </div>
            </div>
          </ClickAwayListener>,
          document.body,
        )}
    </div>
  );
}
